import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "@/hooks/apiHooks/useApi";
import type { Product } from "@models/product";

/**
 * Where the products asked for stand:
 * - `idle`: none asked for;
 * - `reading`: a read is out, or not yet started under the current refresh key;
 * - `failed`: every read has landed, and a product's latest read failed (its older figures,
 *   if any, are still in `products`);
 * - `succeeded`: every product's latest read loaded.
 */
export type CartProductsStatus = "idle" | "reading" | "succeeded" | "failed";

// One product's reads.
interface ProductRead {
    // The latest figures that loaded — kept when a later read fails.
    product?: Product;
    // The latest read started for this product, and the refresh key it was started under.
    requested: number;
    refreshKey: number;
    // The read that settled into this record (0: none yet), and whether it failed.
    landed: number;
    failed: boolean;
}

const isProduct = (value: unknown): value is Product =>
    typeof value === "object" && value !== null;

/**
 * Each cart product's GET /products/{id}, by id (#443): its image, and its vendor rows'
 * stock — the cart payload carries neither. The Cart and Checkout read through this one
 * hook, so their stock gates cannot disagree.
 *
 * A product is read when it has no answer under the current `refreshKey`, or its last read
 * failed. So a new product in the cart is read on its own, and bumping `refreshKey` re-reads
 * every product. A read writes its product only while it is that product's latest read and
 * the hook is mounted, and a failed read keeps the product's older figures.
 */
export const useCartProducts = (
    productIds: string[],
    refreshKey = 0,
): {
    products: Map<string, Product>;
    status: CartProductsStatus;
    // The products whose latest read failed (B-04: a line says its own product's read
    // failed). A product being read again is not in it.
    failed: ReadonlySet<string>;
    // Reads the failed products again, and only those (PR 356 review: a failed line's
    // Retry). The products that loaded keep their figures and are not read again.
    retryFailed: () => void;
} => {
    const { callApi } = useApi();
    // Bumped by retryFailed to run the read effect again: it reads every product whose
    // last read failed, so nothing else needs to change.
    const [retryAttempt, setRetryAttempt] = useState(0);
    const retryFailed = useCallback(() => {
        setRetryAttempt((attempt) => attempt + 1);
    }, []);
    const idsKey = [...new Set(productIds)].sort().join(",");
    // The truth the effect reads; `reads` is its published copy, for rendering.
    const readsRef = useRef(new Map<string, ProductRead>());
    const [reads, setReads] = useState<ReadonlyMap<string, ProductRead>>(
        () => new Map(),
    );
    const sequenceRef = useRef(0);
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const toRead = (idsKey ? idsKey.split(",") : []).filter((id) => {
            const read = readsRef.current.get(id);
            return (
                !read ||
                read.refreshKey !== refreshKey ||
                (read.failed && read.landed === read.requested)
            );
        });

        if (toRead.length === 0) {
            return;
        }

        const sequence = ++sequenceRef.current;
        const publish = () => setReads(new Map(readsRef.current));

        for (const id of toRead) {
            readsRef.current.set(id, {
                landed: 0,
                failed: false,
                ...readsRef.current.get(id),
                requested: sequence,
                refreshKey,
            });
        }
        publish();

        for (const id of toRead) {
            const settle = (product: Product | undefined) => {
                const read = readsRef.current.get(id);
                if (!isMountedRef.current || read?.requested !== sequence) {
                    return;
                }
                readsRef.current.set(id, {
                    ...read,
                    product: product ?? read.product,
                    landed: sequence,
                    failed: product === undefined,
                });
                publish();
            };

            // Plain callApi, deliberately NOT the ship-to wrapper: the fields read here are
            // the image URL, which no destination can change, and the vendor rows' stock
            // (#443), which a destination changes only by blanking a restricted product's
            // rows — that line then shows no stock hint, and checkout still refuses a line
            // above stock. Answering it through the active destination re-issued one request
            // per line on every location switch to recompute a URL that cannot move, and on
            // a placed order asked a historical line about a destination it has nothing to
            // do with. A failure is toasted by useApi.
            callApi<Product>(`/products/${id}`).then(
                (product) => settle(isProduct(product) ? product : undefined),
                () => settle(undefined),
            );
        }
    }, [idsKey, refreshKey, callApi, retryAttempt]);

    return useMemo(() => {
        const ids = idsKey ? idsKey.split(",") : [];
        const products = new Map<string, Product>();
        const failed = new Set<string>();
        let isReading = false;

        for (const id of ids) {
            const read = reads.get(id);
            if (read?.product) {
                products.set(id, read.product);
            }
            if (
                !read ||
                read.refreshKey !== refreshKey ||
                read.landed !== read.requested
            ) {
                isReading = true;
            } else if (read.failed) {
                failed.add(id);
            }
        }

        const status: CartProductsStatus =
            ids.length === 0
                ? "idle"
                : isReading
                    ? "reading"
                    : failed.size > 0
                        ? "failed"
                        : "succeeded";

        return { products, status, failed, retryFailed };
    }, [reads, idsKey, refreshKey, retryFailed]);
};
